import React from "react";
import { Controller } from "react-hook-form";
import TextField from "./TextField";
import { getValueFromObject } from "../../utils/basic";
import { DateTimePicker as MDateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { styled } from "@mui/material";
import { transientOptions } from "../../styles/theme";
import IconButton from "../buttons/IconButton";
import { DesktopDateTimePicker } from "@mui/x-date-pickers";

const DateTimePicker = styled(MDateTimePicker, transientOptions)`
  margin-top: ${({ label }) => (!!label ? "2rem" : "0")};

  &:has(input[readonly]) {
    & .MuiInputBase-input {
      opacity: 1;
      color: rgba(0, 0, 0, 0.38);
    }
  }

  & legend {
    width: 0;
  }
  & .MuiOutlinedInput-notchedOutline {
    border: 0;
  }

  & .MuiFormLabel-root {
    margin: -1rem 0 -5px 4px;
    font-family: ${({ theme }) => theme.fonts.family.default};
    font-size: ${({ theme, size = "normal" }) =>
      theme.fonts.size.inputs[size].label};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.grey._300};
    transform: translateY(-0.75rem) scale(1);
    white-space: unset;
    position: relative;
  }
  & .MuiFormLabel-root.Mui-focused {
    color: ${({ theme }) => theme.colors.grey._800};
  }
  & .MuiFormLabel-root.Mui-error {
    color: ${({ theme }) => theme.colors.error._500};
  }

  & .MuiInputBase-root {
    font-family: ${({ theme }) => theme.fonts.family.default};
    font-size: ${({ theme, size = "normal" }) =>
      theme.fonts.size.inputs[size].content};
    color: ${({ theme }) => theme.colors.grey._900};
    font-weight: 400;
    background-color: ${({ theme }) => theme.colors.grey._50};
    border-radius: ${({ theme }) => theme.borderRadiuses.inputs};
  }
  & .MuiInputBase-root.Mui-error {
    color: ${({ theme }) => theme.colors.error._500};
    background-color: ${({ theme }) => theme.colors.error._50};
  }

  & .MuiFormHelperText-root {
    margin: 4px 0 0 4px;
  }
  & .MuiFormHelperText-root.Mui-error {
    color: ${({ theme }) => theme.colors.error._500};
  }

  & .MuiInputAdornment-root .MuiSvgIcon-root {
    color: ${({ theme }) => theme.colors.grey._900};
  }
  & .MuiInputBase-root.Mui-error .MuiInputAdornment-root .MuiSvgIcon-root {
    color: ${({ theme }) => theme.colors.error._500};
  }
`;

const HelpButton = styled(IconButton)`
  padding: 0;
  margin: -3px 0 0 4px;
  color: ${({ theme }) => theme.colors.grey._300};
  & .MuiSvgIcon-root {
    font-size: ${({ theme }) => theme.fonts.size.default};
  }
`;

const ControlledDateTimePicker = ({
  formProps,
  name,
  validationKey,
  ignoreError = false,
  label,
  ...otherProps
}) => {
  const {
    control,
    formState: { errors },
    rules,
  } = formProps;

  const isError =
    (getValueFromObject(errors, name) !== undefined && !ignoreError) ||
    otherProps.error;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Controller
        name={name}
        control={control}
        disabled={otherProps.disabled}
        rules={
          otherProps.disabled
            ? { a: () => true }
            : getValueFromObject(rules, validationKey ?? name)
        }
        render={({ field: { onChange, onBlur, value } }) => (
          <DateTimePicker
            value={value || null}
            label={label}
            sx={{width: "100%"}}
            viewRenderers={{
              hours: null,
              minutes: null,
              seconds: null,
            }}
            onChange={(v) => {
              onChange(v);
              if (!!otherProps.onChange) {
                otherProps.onChange(v);
              }
            }}
            onBlur={(e) => {
              onBlur();
              if (!!otherProps.onBlur) {
                otherProps.onBlur(value, e);
              }
            }}
          >
            <TextField
              {...otherProps}
              value={value}
              fullWidth
              error={isError}
              helperText={
                !isError
                  ? otherProps.helperText
                  : !ignoreError
                  ? getValueFromObject(errors, name)?.message ??
                    otherProps.helperText
                  : undefined
              }
            />
          </DateTimePicker>
        )}
      />
    </LocalizationProvider>
  );
};

export default ControlledDateTimePicker;
